import { api } from './client';

export const transfersApi = {
  // === 그룹사 간 인력 이동 ===
  getTransfers: async (params?: { employee_id?: string; status?: string; from_company_id?: number; to_company_id?: number }): Promise<any> => {
    const response = await api.get('/api/transfers', { params: params || {} });
    return response.data;
  },

  getTransferDetail: async (transferId: number): Promise<any> => {
    const response = await api.get(`/api/transfers/${transferId}`);
    return response.data;
  },

  createTransfer: async (employeeId: string, data: { to_company_id: number; reason: string; effective_date?: string }): Promise<any> => {
    const response = await api.post(`/api/employees/${encodeURIComponent(employeeId)}/transfer`, data);
    return response.data;
  },

  createTransferRequest: async (employeeId: string, data: { to_company_id: number; reason: string; effective_date: string; transfer_subtype?: string }): Promise<any> => {
    const response = await api.post(`/api/employees/${encodeURIComponent(employeeId)}/transfer`, data);
    return response.data;
  },

  consentTransfer: async (transferId: number, data: { consent: boolean; notes?: string }): Promise<any> => {
    const response = await api.post(`/api/transfers/${transferId}/consent`, data);
    return response.data;
  },

  executeTransfer: async (transferId: number): Promise<any> => {
    const response = await api.post(`/api/transfers/${transferId}/execute`);
    return response.data;
  },

  rollbackTransfer: async (transferId: number, data?: { reason?: string }): Promise<any> => {
    const response = await api.post(`/api/transfers/${transferId}/rollback`, data || {});
    return response.data;
  },

  getTransferSnapshot: async (transferId: number): Promise<any> => {
    const response = await api.get(`/api/transfers/${transferId}/snapshot`);
    return response.data;
  },

  approveTransferFromHr: async (transferId: number, data: { approved: boolean; comment?: string }): Promise<any> => {
    const response = await api.post(`/api/transfers/${transferId}/approve-from-hr`, data);
    return response.data;
  },

  approveTransferToHr: async (transferId: number, data: { approved: boolean; comment?: string }): Promise<any> => {
    const response = await api.post(`/api/transfers/${transferId}/approve-to-hr`, data);
    return response.data;
  },

  approveTransferGroupHr: async (transferId: number, data: { approved: boolean; comment?: string }): Promise<any> => {
    const response = await api.post(`/api/transfers/${transferId}/approve-group-hr`, data);
    return response.data;
  },

  getTransferApprovalStatus: async (transferId: number): Promise<any> => {
    const response = await api.get(`/api/transfers/${transferId}/approval-status`);
    return response.data;
  },

  executeOutboundTransfer: async (transferId: number): Promise<any> => {
    const response = await api.post(`/api/transfers/${transferId}/execute-outbound`);
    return response.data;
  },

  getTransferPermissions: async (): Promise<any> => {
    const response = await api.get('/api/transfer-permissions');
    return response.data;
  },

  checkTransferPermission: async (action: string, fromCompanyId?: number, toCompanyId?: number): Promise<any> => {
    const params: any = { action };
    if (fromCompanyId) params.from_company_id = fromCompanyId;
    if (toCompanyId) params.to_company_id = toCompanyId;
    const response = await api.get('/api/transfer-permissions/check', { params });
    return response.data;
  },

  // === 출향 (BACK-201) ===
  createOutbound: async (data: {
    employee_id: string;
    from_company_id: number;
    to_company_id: number;
    resignation_date: string;
    rehire_date: string;
    new_rank_id?: number;
    new_position?: string;
    return_planned_date?: string;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/api/outbound', data);
    return response.data;
  },

  getOutbounds: async (params?: { company_id?: number; status?: string; employee_id?: string }): Promise<any> => {
    const response = await api.get('/api/outbound', { params });
    return response.data;
  },

  getOutboundDetail: async (outboundId: number): Promise<any> => {
    const response = await api.get(`/api/outbound/${outboundId}`);
    return response.data;
  },

  approveOutbound: async (outboundId: number, step: 'from_hr' | 'to_hr' | 'group'): Promise<any> => {
    const response = await api.post(`/api/outbound/${outboundId}/approve`, { step });
    return response.data;
  },

  executeOutbound: async (outboundId: number): Promise<any> => {
    const response = await api.post(`/api/outbound/${outboundId}/execute`);
    return response.data;
  },

  cancelOutbound: async (outboundId: number, reason: string): Promise<any> => {
    const response = await api.post(`/api/outbound/${outboundId}/cancel`, { reason });
    return response.data;
  },

  requestReturn: async (outboundId: number, return_date: string): Promise<any> => {
    const response = await api.post(`/api/outbound/${outboundId}/return`, { return_date });
    return response.data;
  },

  executeReturn: async (outboundId: number): Promise<any> => {
    const response = await api.post(`/api/outbound/${outboundId}/execute-return`);
    return response.data;
  },

  // === 전보/전직 동의 ===
  getTransferConsentSummary: async (): Promise<any> => {
    const response = await api.get('/api/transfer-consent/summary');
    return response.data;
  },

  getTransferConsentRecords: async (params?: { employee_id?: string; status?: string; transfer_type?: string }): Promise<any> => {
    const response = await api.get('/api/transfer-consent/records', { params: params || {} });
    return response.data;
  },

  createTransferConsent: async (data: { employee_id: string; transfer_type: string; from_value: string; to_value: string; reason?: string; effective_date?: string; notes?: string }): Promise<any> => {
    const response = await api.post('/api/transfer-consent/', data);
    return response.data;
  },

  updateTransferConsent: async (recordId: number, data: { consent_status?: string; consent_date?: string; effective_date?: string; notes?: string }): Promise<any> => {
    const response = await api.put(`/api/transfer-consent/${recordId}`, data);
    return response.data;
  },

  // === 그룹사 가입/탈퇴 (BACK-187) ===
  requestGroupMembership: async (companyId: number, data: any): Promise<any> => {
    const response = await api.post(`/api/companies/${companyId}/membership`, data);
    return response.data;
  },

  approveGroupMembership: async (membershipId: number, data: any): Promise<any> => {
    const response = await api.post(`/api/companies/membership/${membershipId}/approve`, data);
    return response.data;
  },

  requestGroupLeave: async (companyId: number, data: any): Promise<any> => {
    const response = await api.post(`/api/companies/${companyId}/leave`, data);
    return response.data;
  },

  confirmGroupLeave: async (membershipId: number): Promise<any> => {
    const response = await api.post(`/api/companies/membership/${membershipId}/confirm-leave`);
    return response.data;
  },

  getMembershipHistory: async (companyId?: number): Promise<any> => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await api.get('/api/companies/membership/history', { params });
    return response.data;
  },

  checkTenantAccess: async (targetCompanyId: number): Promise<any> => {
    const response = await api.get(`/api/companies/tenant-check/${targetCompanyId}`);
    return response.data;
  },

  // === 권한 위임 (BACK-199) ===
  createDelegation: async (data: any): Promise<any> => {
    const response = await api.post('/api/delegations', data);
    return response.data;
  },

  getDelegations: async (): Promise<any> => {
    const response = await api.get('/api/delegations');
    return response.data;
  },

  revokeDelegation: async (id: number): Promise<any> => {
    const response = await api.post(`/api/delegations/${id}/revoke`);
    return response.data;
  },

  getMyDelegations: async (): Promise<any> => {
    const response = await api.get('/api/delegations/my');
    return response.data;
  },

  getDelegation: async (id: number): Promise<any> => {
    const response = await api.get(`/api/delegations/${id}`);
    return response.data;
  },

  updateDelegation: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/api/delegations/${id}`, data);
    return response.data;
  },

  // === 그룹사 간 개인정보 이전 동의 ===
  getCrossConsentSummary: async (): Promise<any> => {
    const response = await api.get('/api/cross-consent/summary');
    return response.data;
  },

  getCrossConsentByEmployee: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/cross-consent/employee/${encodeURIComponent(employeeId)}`);
    return response.data;
  },

  requestCrossConsent: async (data: { employee_id: string; purpose: string; from_company_id: number; to_company_id: number }): Promise<any> => {
    const response = await api.post('/api/cross-consent/request', data);
    return response.data;
  },

  bulkRequestCrossConsent: async (data: { employee_ids: string[]; purposes: string[]; from_company_id: number; to_company_id: number }): Promise<any> => {
    const response = await api.post('/api/cross-consent/bulk-request', data);
    return response.data;
  },

  respondCrossConsent: async (consentId: number, data: { response: 'consent' | 'refuse'; notes?: string }): Promise<any> => {
    const response = await api.post(`/api/cross-consent/${consentId}/respond`, data);
    return response.data;
  },

  withdrawCrossConsent: async (consentId: number, data?: { reason?: string }): Promise<any> => {
    const response = await api.post(`/api/cross-consent/${consentId}/withdraw`, data || {});
    return response.data;
  },

  getNonConsentedList: async (params?: { purpose?: string }): Promise<any> => {
    const response = await api.get('/api/cross-consent/non-consented', { params: params || {} });
    return response.data;
  },

  getConsentPurposes: async (): Promise<any> => {
    const response = await api.get('/api/cross-consent/purposes');
    return response.data;
  },

  // === 그룹 인재풀 (BACK-132) ===
  getTalentPoolLabels: async (): Promise<any> => {
    const response = await api.get('/api/talent-pool/labels');
    return response.data;
  },

  getTalentPool: async (params?: { company_id?: number; category?: string }): Promise<any> => {
    const response = await api.get('/api/talent-pool', { params: params || {} });
    return response.data;
  },

  getTalentPoolStats: async (companyId?: number): Promise<any> => {
    const response = await api.get('/api/talent-pool/stats', { params: companyId ? { company_id: companyId } : {} });
    return response.data;
  },

  getNineBoxData: async (companyId?: number): Promise<any> => {
    const response = await api.get('/api/talent-pool/nine-box', { params: companyId ? { company_id: companyId } : {} });
    return response.data;
  },

  getTalentPoolEntry: async (entryId: number): Promise<any> => {
    const response = await api.get(`/api/talent-pool/${entryId}`);
    return response.data;
  },

  addToTalentPool: async (data: any): Promise<any> => {
    const response = await api.post('/api/talent-pool', data);
    return response.data;
  },

  updateTalentPoolEntry: async (entryId: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/talent-pool/${entryId}`, data);
    return response.data;
  },

  removeFromTalentPool: async (entryId: number): Promise<any> => {
    const response = await api.delete(`/api/talent-pool/${entryId}`);
    return response.data;
  },

  getSuccessionPlans: async (params?: { company_id?: number; position_title?: string }): Promise<any> => {
    const response = await api.get('/api/talent-pool/succession/plans', { params: params || {} });
    return response.data;
  },

  createSuccessionPlan: async (data: any): Promise<any> => {
    const response = await api.post('/api/talent-pool/succession/plans', data);
    return response.data;
  },

  updateSuccessionPlan: async (planId: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/talent-pool/succession/plans/${planId}`, data);
    return response.data;
  },

  getRotations: async (params?: { employee_id?: string; company_id?: number }): Promise<any> => {
    const response = await api.get('/api/talent-pool/rotations', { params: params || {} });
    return response.data;
  },

  createRotation: async (data: any): Promise<any> => {
    const response = await api.post('/api/talent-pool/rotations', data);
    return response.data;
  },

  updateRotation: async (rotationId: number, data: any): Promise<any> => {
    const response = await api.patch(`/api/talent-pool/rotations/${rotationId}`, data);
    return response.data;
  },
};
